import React, { FC, useMemo } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getFlagUrl } from '../../utils/getFlagUrl';

type MadisonTableProps = {
    values: string[][];
    title?: string;
};

export const MadisonTable: FC<MadisonTableProps> = ({ values, title }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [headers, rows] = useMemo(() => {
        if (!values || values.length === 0) return [[], []];
        const [h, ...r] = values;
        return [h, r];
    }, [values]);

    const countryColIndex = useMemo(() => headers.findIndex((h) => h && h.toLowerCase() === 'country'), [headers]);
    const qColIndex = useMemo(() => headers.findIndex((h) => h && h.toLowerCase() === 'q'), [headers]);
    const groupColumnIndex = 1; // Sloupec B je Team BIB v Madisonu

    // Seskupení dvojic do týmů
    const teams = useMemo(() => {
        if (rows.length === 0) return [];
        const result: string[][][] = [];
        let current: string[][] = [];

        rows.forEach((row, idx) => {
            if (idx > 0 && row[groupColumnIndex] !== rows[idx - 1][groupColumnIndex]) {
                if (current.length > 0) result.push(current);
                current = [];
            }
            current.push(row);
        });
        if (current.length > 0) result.push(current);
        return result;
    }, [rows]);

    const mergeIndices = useMemo(() => {
        const indices = [0, groupColumnIndex]; 
        const pointsIdx = headers.findIndex((h) => h && (h.toLowerCase().includes('points') || h.toLowerCase() === 'total'));
        if (pointsIdx !== -1) indices.push(pointsIdx);
        
        // Slučujeme i náš live sprint sloupec, protože body patří celému týmu!
        const sprintIdx = headers.findIndex((h) => h && h.toLowerCase().includes('sprint') && h.includes('/'));
        if (sprintIdx !== -1) indices.push(sprintIdx);

        return indices;
    }, [headers]);

    const isAbandoned = (value?: string) => value === 'ABD' || value === 'DNF' || value === 'DNS';

    if (!values || values.length === 0 || !values[0]) return null;

    // =========================================================
    // 📱 MOBILNÍ ZOBRAZENÍ (PRO MADISON DVOJICE)
    // =========================================================
    if (isMobile) {
        const pointsIdx = headers.findIndex((h) => h && (h.toLowerCase().includes('points') || h.toLowerCase() === 'total'));
        const sprintCol = headers.findIndex((h) => h && h.toLowerCase().includes('sprint') && h.includes('/'));

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2, mb: 2 }}>
                {title && <Typography variant="h6" fontWeight={700} sx={{ px: 1 }}>{title}</Typography>}
                {teams.map((team, index) => {
                    const firstRider = team[0];
                    if (!firstRider) return null;

                    const rank = team.find(r => r[0] && r[0].trim() !== '')?.[0] || '';
                    const bib = firstRider[groupColumnIndex];
                    const finalValue = pointsIdx !== -1 ? `${firstRider[pointsIdx]} pts` : '';
                    const sprintValue = sprintCol !== -1 ? firstRider[sprintCol] : '';

                    return (
                        <Paper key={index} sx={{ p: 2, borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                {/* LEVÁ ČÁST: Pořadí a číslo týmu */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 45 }}>
                                    {rank && (
                                        <Typography fontWeight={700} sx={{ fontSize: '0.9rem', textAlign: 'center' }}>
                                            {isAbandoned(rank) ? '' : (rank.match(/^\d+$/) ? `#${rank}` : rank)}
                                        </Typography>
                                    )}
                                    {bib && <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 1 }}>{bib}</Typography>}
                                </Box>

                                {/* JEZDCI V TÝMU */}
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {team.map((rider, rIdx) => {
                                        const country = countryColIndex !== -1 ? rider[countryColIndex] : '';
                                        return (
                                            <Box key={rIdx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box>
                                                    <Typography fontWeight={600} sx={{ lineHeight: 1.1 }}>{rider[2] ?? ''}</Typography>
                                                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', pl: 0.5 }}>{rider[3] ?? ''}</Typography>
                                                </Box>
                                                {country && <img src={getFlagUrl(country)} alt={country} style={{ width: '22px', height: '15px', objectFit: 'cover', borderRadius: '2px' }} />}
                                            </Box>
                                        );
                                    })}
                                </Box>

                                {/* 🎯 STŘEDNÍ ČÁST: Sprint X/Y pro tým */}
                                {sprintCol !== -1 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1.5,
                                            backgroundColor: sprintValue && sprintValue !== '0' ? 'rgba(59, 130, 246, 0.12)' : 'transparent',
                                            border: sprintValue && sprintValue !== '0' ? '1px solid rgba(59, 130, 246, 0.3)' : '1px dashed rgba(150, 150, 150, 0.2)',
                                            minWidth: 65,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 700, lineHeight: 1.1, mb: 0.2, whiteSpace: 'nowrap' }}>
                                            {headers[sprintCol]}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: sprintValue && sprintValue !== '0' ? 'primary.main' : 'text.disabled', lineHeight: 1.1 }}>
                                            {sprintValue || '—'}
                                        </Typography>
                                    </Box>
                                )}

                                {/* PRAVÁ ČÁST: Celkové body */}
                                {finalValue && (
                                    <Box sx={{ minWidth: 60, textAlign: 'right' }}>
                                        <Typography fontWeight={700} variant="body2">{finalValue}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        );
    }

    // =========================================================
    // 💻 DESKTOP ZOBRAZENÍ
    // =========================================================
    const sprintColIdx = headers.findIndex((h) => h && h.toLowerCase().includes('sprint') && h.includes('/'));

    return (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3, mt: 2, mb: 2, overflowX: 'auto' }}>
            {title && (
                <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.900', color: 'common.white', textAlign: 'center' }}>
                    <Typography fontWeight={600} variant="subtitle1">{title}</Typography>
                </Box>
            )}
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        {headers.map((header, index) => (
                            <TableCell key={index} align="center" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', backgroundColor: 'grey.900', color: 'common.white', whiteSpace: 'nowrap' }}>
                                {header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {teams.map((team, teamIndex) =>
                        team.map((row, rowIndex) => {
                            const isLastInTeam = rowIndex === team.length - 1;
                            const isLastTeamInTable = teamIndex === teams.length - 1;
                            const currentBorder = isLastInTeam && !isLastTeamInTable ? '3px solid rgba(0, 0, 0, 0.87)' : '1px solid rgba(224, 224, 224, 1)';

                            return (
                                <TableRow key={`${teamIndex}-${rowIndex}`}>
                                    {headers.map((_, cellIndex) => {
                                        const cellValue = row[cellIndex];
                                        const abandoned = isAbandoned(cellValue);
                                        const qualified = cellIndex === qColIndex && cellValue === 'Q';
                                        const isMerged = mergeIndices.includes(cellIndex);
                                        const isLiveSprintCol = cellIndex === sprintColIdx;

                                        if (isMerged && rowIndex > 0) return null;

                                        return (
                                            <TableCell
                                                key={cellIndex}
                                                align="center"
                                                rowSpan={isMerged ? team.length : 1}
                                                sx={{
                                                    verticalAlign: 'middle',
                                                    fontWeight: isLiveSprintCol && cellValue ? 700 : cellIndex === 0 || abandoned ? 700 : 400,
                                                    color: isLiveSprintCol && cellValue ? 'primary.main' : 'inherit',
                                                    backgroundColor: isLiveSprintCol ? 'rgba(59, 130, 246, 0.05)' : 'inherit',
                                                    borderBottom: isMerged ? (!isLastTeamInTable ? '3px solid rgba(0, 0, 0, 0.87)' : '1px solid rgba(224, 224, 224, 1)') : currentBorder
                                                }}
                                            >
                                                {qualified ? <span className="qualified-badge">Q</span> : cellIndex === countryColIndex && cellValue ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src={getFlagUrl(cellValue)} alt={cellValue} style={{ width: '24px' }} /></Box>
                                                ) : cellValue ?? '—'}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};